import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, BarChart3, Target } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const STORAGE_KEY = 'roi_data';

export const ROICalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).investmentAmount : 10000;
  });

  const [returns, setReturns] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).returns : 15000;
  });

  const [timePeriod, setTimePeriod] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timePeriod : 24;
  });

  const [additionalCosts, setAdditionalCosts] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).additionalCosts : 500;
  });

  const [inflationRate, setInflationRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).inflationRate : 3;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      investmentAmount,
      returns,
      timePeriod,
      additionalCosts,
      inflationRate
    }));
  }, [investmentAmount, returns, timePeriod, additionalCosts, inflationRate]);

  const calculateROI = () => {
    const investment = parseFloat(investmentAmount) || 0;
    const gain = parseFloat(returns) || 0;
    const costs = parseFloat(additionalCosts) || 0;
    const months = parseFloat(timePeriod) || 1;
    const inflation = parseFloat(inflationRate) || 0;

    const totalInvestment = investment + costs;
    const netGain = gain - totalInvestment;
    const roiPercentage = totalInvestment > 0 ? (netGain / totalInvestment) * 100 : 0;
    
    // Annualized ROI
    const years = months / 12;
    const annualizedROI = years > 0 ? ((Math.pow(gain / totalInvestment, 1 / years) - 1) * 100) : roiPercentage;
    
    // Payback period (in months)
    const paybackPeriod = netGain > 0 ? (totalInvestment / (netGain / months)) : 0;
    
    // Inflation-adjusted returns
    const realReturnRate = annualizedROI - inflation;
    const inflationAdjustedValue = gain / Math.pow(1 + (inflation / 100), years);
    const realROI = totalInvestment > 0 ? ((inflationAdjustedValue - totalInvestment) / totalInvestment) * 100 : 0;
    
    // Risk-adjusted metrics
    const riskScore = calculateRiskScore(roiPercentage, years);
    const sustainabilityScore = calculateSustainabilityScore(roiPercentage, years);

    return {
      totalInvestment,
      netGain,
      roiPercentage,
      annualizedROI,
      paybackPeriod,
      realReturnRate,
      inflationAdjustedValue,
      realROI,
      riskScore,
      sustainabilityScore
    };
  };

  const calculateRiskScore = (roi, years) => {
    let score = 50;
    
    // Higher ROI increases risk
    if (roi > 50) score += 30;
    else if (roi > 20) score += 20;
    else if (roi > 10) score += 10;
    else if (roi < 0) score += 40;
    
    // Longer time periods increase risk
    if (years > 5) score += 15;
    else if (years > 3) score += 10;
    
    return Math.min(100, score);
  };

  const calculateSustainabilityScore = (roi, years) => {
    let score = 50;
    
    // Sustainable ROI ranges
    if (roi >= 8 && roi <= 15) score += 30;
    else if (roi >= 15 && roi <= 25) score += 20;
    else if (roi > 50) score -= 20; // Too good to be true
    else if (roi < 5) score -= 10;
    
    // Time factor
    if (years >= 3 && years <= 7) score += 20;
    
    return Math.max(0, Math.min(100, score));
  };

  const generateGrowthData = () => {
    const investment = parseFloat(investmentAmount) || 0;
    const costs = parseFloat(additionalCosts) || 0;
    const totalInvestment = investment + costs;
    const finalValue = parseFloat(returns) || 0;
    const months = parseFloat(timePeriod) || 1;
    
    const data = [];
    const monthlyGrowthRate = Math.pow(finalValue / totalInvestment, 1 / months) - 1;
    
    let currentValue = totalInvestment;
    data.push({ month: 0, value: totalInvestment, investment: totalInvestment });
    
    for (let month = 1; month <= months; month++) {
      currentValue = currentValue * (1 + monthlyGrowthRate);
      data.push({ 
        month, 
        value: Math.round(currentValue),
        investment: totalInvestment
      });
    }
    
    return data;
  };

  const generateBreakEvenData = () => {
    const investment = parseFloat(investmentAmount) || 0;
    const costs = parseFloat(additionalCosts) || 0;
    const totalInvestment = investment + costs;
    const finalValue = parseFloat(returns) || 0;
    const months = parseFloat(timePeriod) || 1;
    
    const monthlyReturn = (finalValue - totalInvestment) / months;
    const data = [];
    
    let accumulated = 0;
    for (let month = 0; month <= months; month++) {
      accumulated = monthlyReturn * month;
      data.push({
        month,
        cumulativeReturn: Math.round(accumulated),
        breakEven: 0
      });
    }
    
    return data;
  };

  const generateComparisonData = () => {
    const results = calculateROI();
    
    // Compare with common benchmarks
    const benchmarks = [
      { name: 'Your Investment', roi: results.annualizedROI, fill: 'hsl(var(--primary))' },
      { name: 'Stock Market (S&P 500)', roi: 10, fill: 'hsl(var(--secondary))' },
      { name: 'Real Estate', roi: 8, fill: 'hsl(var(--accent))' },
      { name: 'Bonds', roi: 5, fill: 'hsl(var(--chart-4))' },
      { name: 'Savings Account', roi: 2, fill: 'hsl(var(--chart-5))' }
    ];
    
    return benchmarks;
  };

  const generateRiskReturnData = () => {
    const scenarios = [
      { name: 'Conservative', risk: 20, return: 5 },
      { name: 'Moderate', risk: 40, return: 10 },
      { name: 'Balanced', risk: 50, return: 12 },
      { name: 'Aggressive', risk: 70, return: 18 },
      { name: 'Your Investment', risk: calculateROI().riskScore, return: calculateROI().annualizedROI }
    ];
    
    return scenarios;
  };

  const results = calculateROI();
  const growthData = generateGrowthData();
  const breakEvenData = generateBreakEvenData();
  const comparisonData = generateComparisonData();
  const riskReturnData = generateRiskReturnData();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getRiskLabel = (score) => {
    if (score >= 70) return 'High Risk';
    if (score >= 50) return 'Moderate Risk';
    if (score >= 30) return 'Low-Moderate Risk';
    return 'Low Risk';
  };

  const getRiskColor = (score) => {
    if (score >= 70) return 'text-destructive';
    if (score >= 50) return 'text-accent';
    return 'text-secondary';
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  ROI Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is ROI?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Return on Investment (ROI) measures the profitability of an investment relative to its cost. 
                  It's the most important metric for evaluating investment performance and comparing different opportunities.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Investment Details</CardTitle>
                  <CardDescription>Enter your investment parameters</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="investmentAmount" className="text-sm font-medium">
                      Investment Amount ($)
                    </Label>
                    <Input
                      id="investmentAmount"
                      type="number"
                      value={investmentAmount}
                      onChange={(e) => setInvestmentAmount(e.target.value)}
                      placeholder="10000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Initial capital invested</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="returns" className="text-sm font-medium">
                      Final Value / Returns ($)
                    </Label>
                    <Input
                      id="returns"
                      type="number"
                      value={returns}
                      onChange={(e) => setReturns(e.target.value)}
                      placeholder="15000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Expected or actual final value</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timePeriod" className="text-sm font-medium">
                      Time Period (Months)
                    </Label>
                    <Input
                      id="timePeriod"
                      type="number"
                      value={timePeriod}
                      onChange={(e) => setTimePeriod(e.target.value)}
                      placeholder="24"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">{(timePeriod / 12).toFixed(1)} years</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additionalCosts" className="text-sm font-medium">
                      Additional Costs ($)
                    </Label>
                    <Input
                      id="additionalCosts"
                      type="number"
                      value={additionalCosts}
                      onChange={(e) => setAdditionalCosts(e.target.value)}
                      placeholder="500"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Fees, taxes, maintenance, etc.</p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="inflationRate" className="text-sm font-medium">
                      Inflation Rate (% per year)
                    </Label>
                    <Input
                      id="inflationRate"
                      type="number"
                      value={inflationRate}
                      onChange={(e) => setInflationRate(e.target.value)}
                      placeholder="3"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">For inflation-adjusted returns</p>
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Investment Performance</CardTitle>
                    <CardDescription>Your return metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">ROI Percentage</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${
                          results.roiPercentage >= 0 ? 'text-accent' : 'text-destructive'
                        }`}>
                          {results.roiPercentage >= 0 ? '+' : ''}{results.roiPercentage.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {results.roiPercentage >= 0 ? 'Profitable return' : 'Loss on investment'}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Annualized ROI</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {results.annualizedROI >= 0 ? '+' : ''}{results.annualizedROI.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Per year</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Gain</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${
                          results.netGain >= 0 ? 'text-secondary' : 'text-destructive'
                        }`}>
                          {formatCurrency(results.netGain)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Profit or loss</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Payback Period</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.paybackPeriod > 0 ? `${results.paybackPeriod.toFixed(1)}` : 'N/A'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {results.paybackPeriod > 0 ? 'months to recover' : 'No recovery'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Advanced Metrics</CardTitle>
                    <CardDescription>Risk and inflation-adjusted analysis</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Inflation-Adjusted ROI</span>
                            <span className="text-lg font-display font-bold text-foreground">
                              {results.realROI.toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-accent transition-all duration-300"
                              style={{ width: `${Math.max(0, Math.min(100, results.realROI))}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Real purchasing power gain after {inflationRate}% inflation
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Real Return Rate</span>
                            <span className="text-lg font-display font-bold text-foreground">
                              {results.realReturnRate.toFixed(1)}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Annualized return minus inflation
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Risk Score</span>
                            <span className={`text-lg font-display font-bold ${getRiskColor(results.riskScore)}`}>
                              {results.riskScore}/100
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 ${
                                results.riskScore >= 70 ? 'bg-destructive' :
                                results.riskScore >= 50 ? 'bg-accent' :
                                'bg-secondary'
                              }`}
                              style={{ width: `${results.riskScore}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {getRiskLabel(results.riskScore)}
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-muted-foreground">Sustainability Score</span>
                            <span className="text-lg font-display font-bold text-secondary">
                              {results.sustainabilityScore}/100
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-secondary transition-all duration-300"
                              style={{ width: `${results.sustainabilityScore}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Likelihood of maintaining returns
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Investment Growth Over Time</CardTitle>
                <CardDescription className="text-base">
                  Projected value growth from initial investment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={growthData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--accent))" 
                        strokeWidth={3}
                        fillOpacity={1} 
                        fill="url(#colorValue)"
                        name="Investment Value"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="investment" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Initial Investment"
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Break-Even Recovery Point</CardTitle>
                  <CardDescription>
                    When your investment becomes profitable
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={breakEvenData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="cumulativeReturn" 
                          stroke="hsl(var(--secondary))" 
                          strokeWidth={3}
                          name="Cumulative Returns"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="breakEven" 
                          stroke="hsl(var(--destructive))" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Break-Even Line"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Comparison with Alternatives</CardTitle>
                  <CardDescription>
                    Benchmark against common investment options
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={100}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Annualized ROI (%)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => `${value.toFixed(1)}%`}
                        />
                        <Bar dataKey="roi" radius={[8, 8, 0, 0]}>
                          {comparisonData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Risk vs Return Analysis</CardTitle>
                <CardDescription>
                  Where your investment stands in the risk-return spectrum
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={riskReturnData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        yAxisId="left"
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Risk Score', angle: -90, position: 'insideLeft' }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Return (%)', angle: 90, position: 'insideRight' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="risk" fill="hsl(var(--destructive))" name="Risk Score" radius={[8, 8, 0, 0]} />
                      <Bar yAxisId="right" dataKey="return" fill="hsl(var(--secondary))" name="Expected Return %" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding ROI</CardTitle>
                <CardDescription className="text-base">
                  Learn how to interpret your returns and make better investment decisions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Your ROI: {results.roiPercentage.toFixed(1)}%
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    You invested <strong className="text-accent">{formatCurrency(results.totalInvestment)}</strong> and 
                    your investment is worth <strong className="text-accent">{formatCurrency(parseFloat(returns))}</strong>, 
                    giving you a return of <strong className="text-accent">{results.roiPercentage.toFixed(1)}%</strong>.
                    {results.roiPercentage >= 0 ? (
                      <> This means your investment is profitable with a gain of {formatCurrency(results.netGain)}.</>
                    ) : (
                      <> This indicates a loss of {formatCurrency(Math.abs(results.netGain))}. Review if this investment aligns with your goals.</>
                    )}
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">ROI = [(Final Value - Total Investment) / Total Investment] × 100</p>
                    <p className="font-mono">
                      ROI = [({formatCurrency(parseFloat(returns))} - {formatCurrency(results.totalInvestment)}) / {formatCurrency(results.totalInvestment)}] × 100 = {results.roiPercentage.toFixed(1)}%
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Annualized ROI: {results.annualizedROI.toFixed(1)}%
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your annualized ROI of <strong className="text-primary">{results.annualizedROI.toFixed(1)}%</strong> represents 
                    the average return per year over {(timePeriod / 12).toFixed(1)} years. This is the best metric for comparing 
                    investments with different time horizons.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">Performance Rating:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        results.annualizedROI >= 15 ? 'bg-secondary/20 text-secondary' :
                        results.annualizedROI >= 8 ? 'bg-primary/20 text-primary' :
                        results.annualizedROI >= 5 ? 'bg-accent/20 text-accent' :
                        'bg-destructive/20 text-destructive'
                      }`}>
                        {results.annualizedROI >= 15 ? 'Excellent' :
                         results.annualizedROI >= 8 ? 'Good' :
                         results.annualizedROI >= 5 ? 'Fair' :
                         'Poor'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {results.annualizedROI >= 15 ? 
                        'Outstanding returns! This significantly outperforms market averages (~10%).' :
                       results.annualizedROI >= 8 ?
                        'Solid returns that match or slightly beat typical market performance.' :
                       results.annualizedROI >= 5 ?
                        'Moderate returns. Consider if this meets your financial goals and risk tolerance.' :
                        'Below-average returns. Evaluate if this investment is worth continuing.'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Inflation-Adjusted Analysis
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your real ROI (after {inflationRate}% inflation) is <strong className="text-secondary">{results.realROI.toFixed(1)}%</strong>. 
                    This represents your actual purchasing power gain. A positive real ROI means your money is growing faster than 
                    inflation, preserving and building wealth.
                  </p>
                  <div className="space-y-3">
                    <div className="p-4 rounded-lg bg-secondary-light/50 border border-secondary/20">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">Nominal Return</span>
                        <span className="text-lg font-bold text-secondary">{results.annualizedROI.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">Minus Inflation</span>
                        <span className="text-lg font-bold text-destructive">-{inflationRate}%</span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold">Real Return</span>
                        <span className="text-xl font-display font-bold text-foreground">{results.realReturnRate.toFixed(1)}%</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      💡 Inflation-adjusted value: Your {formatCurrency(parseFloat(returns))} is equivalent to {formatCurrency(results.inflationAdjustedValue)} in today's purchasing power.
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Risk Assessment: {getRiskLabel(results.riskScore)}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your investment has a risk score of <strong className={getRiskColor(results.riskScore)}>{results.riskScore}/100</strong>. 
                    {results.riskScore >= 70 ? (
                      <> High-risk investments can offer great returns but may also result in significant losses. Ensure this aligns with your risk tolerance.</>
                    ) : results.riskScore >= 50 ? (
                      <> Moderate risk is typical for balanced portfolios. Diversification can help manage this risk level.</>
                    ) : (
                      <> Lower risk investments provide more stability but may offer modest returns. Good for conservative strategies.</>
                    )}
                  </p>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Volatility Risk:</strong> Returns above 25% annually are often 
                        unsustainable long-term. High returns may come with high volatility or hidden risks.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Time Horizon Risk:</strong> Longer investment periods (5+ years) 
                        generally carry higher uncertainty about economic conditions and market changes.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Concentration Risk:</strong> Don't put all your capital in one 
                        investment. Diversify across asset classes, industries, and geographies.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Return Sustainability Analysis
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your sustainability score is <strong className="text-purple">{results.sustainabilityScore}/100</strong>. 
                    This indicates how likely your investment returns can be maintained over time.
                  </p>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Sustainable Returns (8-15% annually)</h4>
                      <p className="text-muted-foreground">
                        These align with historical market averages for diversified equity portfolios. Achievable through 
                        index funds, real estate, or stable businesses.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">⚡ Aggressive Returns (15-25% annually)</h4>
                      <p className="text-muted-foreground">
                        Possible with concentrated bets, growth stocks, or leverage. Requires expertise and higher risk tolerance. 
                        Difficult to maintain for 5+ years.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-destructive/10 border-l-4 border-destructive">
                      <h4 className="font-semibold text-foreground mb-2">🚨 Exceptional Returns (25%+ annually)</h4>
                      <p className="text-muted-foreground">
                        Extremely rare to sustain long-term. Often indicates high risk, timing luck, or unsustainable business models. 
                        Be cautious of "too good to be true" promises.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Investment Decision Framework
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left font-semibold">Metric</th>
                          <th className="p-3 text-left font-semibold">Your Value</th>
                          <th className="p-3 text-left font-semibold">Benchmark</th>
                          <th className="p-3 text-left font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="p-3 font-medium">ROI %</td>
                          <td className="p-3">{results.roiPercentage.toFixed(1)}%</td>
                          <td className="p-3">&gt; 8%</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              results.roiPercentage >= 8 ? 'bg-secondary/20 text-secondary' : 'bg-destructive/20 text-destructive'
                            }`}>
                              {results.roiPercentage >= 8 ? '✓ Good' : '✗ Below'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Annualized ROI</td>
                          <td className="p-3">{results.annualizedROI.toFixed(1)}%</td>
                          <td className="p-3">&gt; 10%</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              results.annualizedROI >= 10 ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'
                            }`}>
                              {results.annualizedROI >= 10 ? '✓ Excellent' : '~ Moderate'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Real ROI</td>
                          <td className="p-3">{results.realROI.toFixed(1)}%</td>
                          <td className="p-3">&gt; 5%</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              results.realROI >= 5 ? 'bg-secondary/20 text-secondary' : 'bg-destructive/20 text-destructive'
                            }`}>
                              {results.realROI >= 5 ? '✓ Beating Inflation' : '✗ Losing Value'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Risk Score</td>
                          <td className="p-3">{results.riskScore}/100</td>
                          <td className="p-3">&lt; 60</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              results.riskScore < 60 ? 'bg-secondary/20 text-secondary' : 'bg-destructive/20 text-destructive'
                            }`}>
                              {results.riskScore < 60 ? '✓ Manageable' : '! High Risk'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Payback Period</td>
                          <td className="p-3">{results.paybackPeriod > 0 ? `${results.paybackPeriod.toFixed(1)} mo` : 'N/A'}</td>
                          <td className="p-3">&lt; 24 months</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              results.paybackPeriod > 0 && results.paybackPeriod < 24 ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'
                            }`}>
                              {results.paybackPeriod > 0 && results.paybackPeriod < 24 ? '✓ Fast Recovery' : '~ Slow'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ROICalculator;
